import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import axios from "../axiosConfig";
import Modal from "./Modal";
import ParticipantList from "./ParticipantList";
import Table from "./Table";
import ContentPresets from "./ContentPresets";
import NewTaskModal, { taskCopyValues } from "./NewTaskModal";
import {
  getLocalDatetimeInputValue,
  toUTCISOString,
  toLocalISOString,
  useFieldOptions,
  fieldOptionList,
} from "./Utils";

const InteractionsTable = forwardRef(
  (
    {
      contactId,
      accountId,
      jobId,
      defaultParticipants = [],
      onTaskCreated,
      fillPage = false,
    },
    ref,
  ) => {
    const [interactions, setInteractions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);
    const fieldOptions = useFieldOptions();
    const [validationErrors, setValidationErrors] = useState({});

    useEffect(() => {
      try {
        const storedUser = JSON.parse(localStorage.getItem("user"));
        if (storedUser) {
          setUser(storedUser);
        }
      } catch {
        console.warn("Invalid user in localStorage");
      }
    }, []);

    useEffect(() => {
      if (user?.settings) {
        setFormData((prev) => ({
          ...prev,
          format: user.settings.default_interaction_format || "",
          type: user.settings.default_interaction_type || "",
        }));
      }
    }, [user]);

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingInteraction, setEditingInteraction] = useState(null);
    // Initial values of the new task being copied from an interaction, or null.
    const [taskCopy, setTaskCopy] = useState(null);

    const [formData, setFormData] = useState({
      completedAtLocal: getLocalDatetimeInputValue(),
      format: "", // temporary until user loads
      type: "", // temporary until user loads
      content: "",
      participants: defaultParticipants,
    });

    const resetForm = () => {
      setFormData({
        completedAtLocal: getLocalDatetimeInputValue(),
        format: user?.settings?.default_interaction_format || "",
        type: user?.settings?.default_interaction_type || "",
        content: "",
        participants: defaultParticipants,
      });
    };

    const validateRequired = () => {
      const errors = {};
      if (!String(formData.completedAtLocal || "").trim())
        errors.completedAtLocal = true;
      if (!String(formData.format || "").trim()) errors.format = true;
      if (!String(formData.type || "").trim()) errors.type = true;
      if (!String(formData.content || "").trim()) errors.content = true;
      return errors;
    };

    useEffect(() => {
      fetchInteractions();
    }, [contactId, accountId, jobId]);

    // Expose fetchInteractions to parent component
    useImperativeHandle(
      ref,
      () => ({
        fetchInteractions,
      }),
      [],
    );

    const fetchInteractions = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (contactId) params.append("contact_id", contactId);
        if (accountId) params.append("account_id", accountId);
        if (jobId) params.append("job_id", jobId);

        const res = await axios.get(`/interactions/?${params.toString()}`);

        // Process interactions to split participants into separate arrays
        const processedInteractions = (res.data || []).map((interaction) => ({
          ...interaction,
          accountParticipants:
            interaction.participants?.filter((p) => p.type === "account") || [],
          contactParticipants:
            interaction.participants?.filter((p) => p.type === "contact") || [],
        }));

        setInteractions(processedInteractions);
      } catch (err) {
        console.error("Error fetching interactions:", err);
      } finally {
        setLoading(false);
      }
    };

    const openCreateModal = () => {
      resetForm();
      setValidationErrors({});
      setShowCreateModal(true);
    };

    const openEditModal = (interaction) => {
      setFormData({
        completedAtLocal: toLocalISOString(
          interaction.completed_at || interaction.created_at,
        ),
        format: interaction.format || "",
        type: interaction.type || "",
        content: interaction.content || "",
        participants: interaction.participants || [],
      });
      setValidationErrors({});
      setEditingInteraction(interaction);
    };

    const handleFormChange = (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));

      setValidationErrors((prev) => {
        if (!prev[name]) return prev;
        const next = { ...prev };
        delete next[name];
        return next;
      });
    };

    const handlePresetContent = (content) => {
      handleFormChange({ target: { name: "content", value: content } });
    };

    const handleParticipantsChange = (participants) => {
      setFormData((prev) => ({ ...prev, participants }));
    };

    const buildPayload = () => ({
      completed_at: toUTCISOString(formData.completedAtLocal),
      content: formData.content,
      format: formData.format,
      type: formData.type,
      job_id: jobId || null,
      participants: formData.participants.map((p) => ({
        id: p.id,
        type: p.type,
        is_sender: p.is_sender || false,
      })),
    });

    const handleCreateSubmit = async (e) => {
      e.preventDefault();
      const requiredErrors = validateRequired();
      if (Object.keys(requiredErrors).length > 0) {
        setValidationErrors(requiredErrors);
        return;
      }
      try {
        await axios.post("/interactions/", buildPayload(), {
          withCredentials: true,
        });
        setShowCreateModal(false);
        setValidationErrors({});
        fetchInteractions();
      } catch (err) {
        console.error("Failed to create interaction:", err);
      }
    };

    const handleEditSubmit = async (e) => {
      console.log("handleEditSubmit called, formData:", formData);
      e.preventDefault();
      if (!editingInteraction) return;
      const requiredErrors = validateRequired();
      if (Object.keys(requiredErrors).length > 0) {
        setValidationErrors(requiredErrors);
        return;
      }
      try {
        await axios.patch(
          `/interactions/${editingInteraction.id}/`,
          buildPayload(),
          { withCredentials: true },
        );
        setEditingInteraction(null);
        setValidationErrors({});
        fetchInteractions();
      } catch (err) {
        console.error("Failed to update interaction:", err);
      }
    };

    const handleSaveAsDraft = async (e) => {
      e.preventDefault();
      if (!editingInteraction) return;
      try {
        const payload = {
          ...buildPayload(),
          completed_at: null, // Set completed_at to null for draft
        };
        await axios.patch(`/interactions/${editingInteraction.id}/`, payload, {
          withCredentials: true,
        });
        setEditingInteraction(null);
        fetchInteractions();
        // Notify parent that a task was created from an interaction
        if (onTaskCreated) {
          onTaskCreated();
        }
      } catch (err) {
        console.error("Failed to save interaction as draft:", err);
      }
    };

    const handleDelete = async () => {
      if (!editingInteraction) return;
      try {
        await axios.delete(`/interactions/${editingInteraction.id}/`, {
          withCredentials: true,
        });
        setEditingInteraction(null);
        fetchInteractions();
      } catch (err) {
        console.error("Failed to delete interaction:", err);
      }
    };

    // Define table columns
    const columns = [
      {
        key: "completed_at",
        header: "Date",
        type: "date",
        width: 80,
      },
      {
        key: "content",
        header: "Content",
        type: "text",
        truncate: 200,
        width: 256,
      },
      {
        key: "accountParticipants",
        header: "Accounts",
        type: "participant",
        sortable: false,
        ftlInitials: true,
        width: 128,
      },
      {
        key: "contactParticipants",
        header: "Contacts",
        type: "participant",
        sortable: false,
        ftlInitials: true,
        width: 128,
      },
      {
        key: "type",
        header: "Type",
        type: "discrete",
        width: 80,
      },
      {
        key: "format",
        header: "Format",
        type: "discrete",
        width: 80,
      },
      {
        key: "actions",
        header: "Actions",
        type: "static",
        sortable: false,
        width: 64,
        render: (value, row) => (
          <div onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setTaskCopy(taskCopyValues(row))}
              title="New task with the same details"
            >
              Copy
            </button>
          </div>
        ),
      },
    ];

    return (
      <div
        className="interactions-container"
        style={
          fillPage
            ? {
                height: "100%",
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
              }
            : undefined
        }
      >
        <div className="interactions-header">
          <button onClick={openCreateModal}>+ Log Interaction</button>
        </div>
        {/* Create Modal */}
        <Modal
          isOpen={showCreateModal}
          onClose={() => {
            setShowCreateModal(false);
            setValidationErrors({});
          }}
          title="Log New Interaction"
        >
          <form onSubmit={handleCreateSubmit} className="interaction-form">
            <label>
              Date/Time: <em>(required)</em>
            </label>
            <input
              autoComplete="off"
              type="datetime-local"
              name="completedAtLocal"
              value={formData.completedAtLocal}
              onChange={handleFormChange}
              className={
                validationErrors.completedAtLocal ? "required-invalid" : ""
              }
            />

            <label>
              Format: <em>(required)</em>
            </label>
            <select
              autoComplete="off"
              name="format"
              value={formData.format}
              onChange={handleFormChange}
              className={validationErrors.format ? "required-invalid" : ""}
            >
              <option value="">Select format...</option>
              {fieldOptionList(fieldOptions, "interactions", "format").map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>

            <label>
              Type: <em>(required)</em>
            </label>
            <select
              autoComplete="off"
              name="type"
              value={formData.type}
              onChange={handleFormChange}
              className={validationErrors.type ? "required-invalid" : ""}
            >
              <option value="">Select type...</option>
              {fieldOptionList(fieldOptions, "interactions", "type").map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>

            <label>
              Content: <em>(required)</em>
            </label>
            <textarea
              autoComplete="off"
              name="content"
              value={formData.content}
              onChange={handleFormChange}
              rows={4}
              className={validationErrors.content ? "required-invalid" : ""}
            />
            <ContentPresets onSelect={handlePresetContent} />

            <ParticipantList
              participants={formData.participants}
              onChange={handleParticipantsChange}
            />

            <div className="modal-footer">
              <button type="submit" className="submit-btn">
                Submit
              </button>
            </div>
          </form>
        </Modal>
        {/* Edit Modal */}
        <Modal
          isOpen={!!editingInteraction}
          onClose={() => {
            setEditingInteraction(null);
            setValidationErrors({});
          }}
          title="Edit Interaction"
        >
          {editingInteraction && (
            <>
              {editingInteraction.logger &&
                (editingInteraction.logger.first_name ||
                  editingInteraction.logger.last_name) && (
                  <div
                    style={{
                      fontStyle: "italic",
                      marginBottom: "15px",
                      color: "#666",
                    }}
                  >
                    Created by: {editingInteraction.logger.first_name}{" "}
                    {editingInteraction.logger.last_name}
                  </div>
                )}
              <form onSubmit={handleEditSubmit} className="interaction-form">
                <label>
                  Date/Time: <em>(required)</em>
                </label>
                <input
                  autoComplete="off"
                  type="datetime-local"
                  name="completedAtLocal"
                  value={formData.completedAtLocal}
                  onChange={handleFormChange}
                  className={
                    validationErrors.completedAtLocal ? "required-invalid" : ""
                  }
                />

                <label>
                  Format: <em>(required)</em>
                </label>
                <select
                  autoComplete="off"
                  name="format"
                  value={formData.format}
                  onChange={handleFormChange}
                  className={validationErrors.format ? "required-invalid" : ""}
                >
                  <option value="">Select format...</option>
                  {fieldOptionList(fieldOptions, "interactions", "format").map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>

                <label>
                  Type: <em>(required)</em>
                </label>
                <select
                  autoComplete="off"
                  name="type"
                  value={formData.type}
                  onChange={handleFormChange}
                  className={validationErrors.type ? "required-invalid" : ""}
                >
                  <option value="">Select type...</option>
                  {fieldOptionList(fieldOptions, "interactions", "type").map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>

                <label>
                  Content: <em>(required)</em>
                </label>
                <textarea
                  autoComplete="off"
                  name="content"
                  value={formData.content}
                  onChange={handleFormChange}
                  rows={4}
                  className={validationErrors.content ? "required-invalid" : ""}
                />
                <ContentPresets onSelect={handlePresetContent} />

                <ParticipantList
                  participants={formData.participants}
                  onChange={handleParticipantsChange}
                />

                <div className="modal-footer">
                  <button type="submit" className="submit-btn">
                    Save Changes
                  </button>
                  <button type="button" onClick={handleSaveAsDraft}>
                    MAKE TASK
                  </button>
                  <button
                    type="button"
                    className="delete-btn"
                    onClick={handleDelete}
                  >
                    Delete
                  </button>
                </div>
              </form>
            </>
          )}
        </Modal>{" "}
        <NewTaskModal
          isOpen={!!taskCopy}
          onClose={() => setTaskCopy(null)}
          initialValues={taskCopy}
          jobId={jobId}
          onCreated={() => onTaskCreated && onTaskCreated()}
        />
        {/* Table */}
        <Table
          data={interactions}
          columns={columns}
          loading={loading}
          emptyMessage="No interactions found."
          onRowClick={(interaction) => openEditModal(interaction)}
          defaultSortColumn="completed_at"
          defaultSortDirection="desc"
          fillPage={fillPage}
        />
      </div>
    );
  },
);

export default InteractionsTable;
